import {
  WSAvatar,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSPanel,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { queriesPayerRows, queriesPayers } from "@query";
import { PayeeRowAutoPayStrategy } from "@wingspanhq/wingspan-api-sdk";
import { FieldView } from "../../../../shared/components/FieldView/FieldView";
import { selectorPaymentMethodLabel } from "../../selectors/selectorPaymentMethodLabel";
import styles from "./styles.module.scss";

export interface AutoPayProps extends WSElementProps {
  payerId: string;
}

/**
 * Whether this client pays automatically, from the client's own v3 settings.
 *
 * The panel is absent while autopay is off: v1 used that state to offer a setup
 * link built from the payee's tag, and `/v3/platform/tags` answers 501, so an
 * off-state panel would carry no action.
 */
export const AutoPay: React.FC<AutoPayProps> = ({
  payerId,
  ...elementProps
}) => {
  // SDK 3.6.0 dropped `isAutoPayEnabled` from the payer settings read — the
  // flag is now payee-scoped (`PayeeDefaults` / `PayeeEngagementAutoPayOverrides`).
  // The client row carries the resolved answer for this payer as
  // `autoPayStrategy`, and no direct endpoint exposes it, so the row search is
  // the only source — this panel is the one read on the client-detail screen
  // that legitimately still needs `/v3/search/payer-rows`.
  //
  // Narrowed by `payerAccountId`, NOT by `id`: the server answers every `id`
  // form with 422 `{"field":"filter","code":"InvalidFormat"}` — both `eq` and
  // `anyOf`, though the SDK's `SearchPayerRowsFilterParameterId` declares both.
  // That mismatch failed this query closed, so the panel rendered as "autopay
  // off" for every client regardless of the real setting.
  //
  // `getPayer` supplies the account id and shares its query key with the
  // client-detail screen's own read, so this costs no extra request.
  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId },
    { enabled: !!payerId }
  );
  const payerAccountId = queryPayer.data?.payerAccountId;
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    { filter: { payerAccountId: { eq: payerAccountId as string } } },
    { enabled: !!payerAccountId }
  );

  const isAutoPayEnabled =
    queryPayerRow.data?.data?.[0]?.autoPayStrategy ===
    PayeeRowAutoPayStrategy.ALL;

  // SDK 2.8.0 dropped `defaultPaymentMethod` from the payer settings read, so the
  // default instrument is identified by the collection itself — the method
  // flagged `isDefault`. An internal account carries no such flag and can never
  // be the default here.
  const queryPaymentMethods = queriesPayers.listPayerPaymentMethods.useQuery(
    { payerId },
    { enabled: isAutoPayEnabled, retry: false }
  );

  if (!isAutoPayEnabled) {
    return null;
  }

  const defaultPaymentMethod = (queryPaymentMethods.data?.data ?? []).find(
    method => "isDefault" in method && method.isDefault
  );
  const paymentMethodLabel = defaultPaymentMethod
    ? selectorPaymentMethodLabel(defaultPaymentMethod)
    : undefined;

  return (
    <WSElement {...elementProps}>
      <WSSectionToolbar
        title="Autopay"
        pill={{ theme: "success", text: "On", badge: true }}
      />

      <WSPanel className={styles.panel}>
        <WSText.ParagraphSm weight="book" mb="XL">
          Each invoice you send will be automatically paid on the due date
          unless this client disables Autopay.
        </WSText.ParagraphSm>

        <FieldView
          label="Payment Method"
          value={
            paymentMethodLabel ? (
              <WSFlexBox.CenterY gap="S">
                <WSAvatar.Icon
                  icon={paymentMethodLabel.icon}
                  size="M"
                  color="gray500"
                />
                <WSText.ParagraphSm weight="book" color="gray600">
                  {paymentMethodLabel.text}
                </WSText.ParagraphSm>
              </WSFlexBox.CenterY>
            ) : (
              "None"
            )
          }
        />
      </WSPanel>
    </WSElement>
  );
};
